import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { usePermissions } from '@/auth/usePermissions';
import type { TransferRow, TransferDialogResult } from '@/types/monitoring';
import {
  useCheckTransactionCorrectionMutation,
  useCancelTransferMutation,
  useCorrectFailedTransferMutation,
} from './api';
import { getProviderIconSrc } from './providerIcon';

const FAILED_CODES = [3, 8, 18, 19, 44, 50];

interface Props {
  transfer: TransferRow;
  onClose: (result: TransferDialogResult | null) => void;
}

type View = 'details' | 'cancel' | 'correct';

export function TransferDetailsModal({ transfer, onClose }: Props) {
  const { t } = useTranslation();
  const { hasAny } = usePermissions();
  const [view, setView] = useState<View>('details');
  const [ticketId, setTicketId] = useState('');
  const [externalReference, setExternalReference] = useState('');

  const checkMutation = useCheckTransactionCorrectionMutation();
  const cancelMutation = useCancelTransferMutation();
  const correctMutation = useCorrectFailedTransferMutation();

  const iconSrc = getProviderIconSrc(transfer.provider, transfer.serviceType);

  const handleCancelClick = async () => {
    const res = await checkMutation.mutateAsync({
      transactionId: transfer.internalReference,
      transactionType: 'TRANSFER',
    });
    if (res.code === 0) {
      setView('cancel');
    } else {
      onClose({ code: res.code, message: res.message });
    }
  };

  const handleCorrectClick = async () => {
    const res = await checkMutation.mutateAsync({
      transactionId: transfer.internalReference,
      transactionType: 'TRANSFER',
    });
    if (res.code === 0) {
      setView('correct');
    } else {
      onClose({ code: res.code, message: res.message });
    }
  };

  const handleCancelSend = async () => {
    const res = await cancelMutation.mutateAsync({
      transferId: transfer.internalReference,
      ticketId,
    });
    onClose({ code: res.code, message: res.message, type: 'cancel' });
  };

  const handleCorrectSend = async () => {
    const res = await correctMutation.mutateAsync({
      transferId: transfer.internalReference,
      ticketId,
      externalRef: externalReference,
    });
    onClose({ code: res.code, message: res.message, type: 'correct' });
  };

  const DetailRow = ({ label, value }: { label: string; value: string | null | undefined }) => (
    <ListItem disableGutters>
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value ?? '—'}
        </Typography>
      </Stack>
    </ListItem>
  );

  return (
    <Dialog open onClose={() => onClose(null)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center' }}>
        {iconSrc && (
          <Box
            component="img"
            src={iconSrc}
            alt="provider"
            sx={{ width: 32, height: 32, mr: 1, verticalAlign: 'middle' }}
          />
        )}
        {moment(transfer.date).format('DD/MM/YYYY H:mm')}
      </DialogTitle>
      <DialogContent>
        {view === 'details' && (
          <>
            <List dense>
              <DetailRow
                label={t('pages.monitoring.transfers.table.operation', { defaultValue: 'Operation' })}
                value={t(`pages.monitoring.transfers.${transfer.operation}`, {
                  defaultValue: transfer.operation,
                })}
              />
              <DetailRow
                label={t('pages.monitoring.transfers.table.receiver', { defaultValue: 'Receiver' })}
                value={transfer.receiver}
              />
              <DetailRow
                label={t('pages.monitoring.transfers.table.sender', { defaultValue: 'Sender' })}
                value={transfer.sender}
              />
              <DetailRow
                label={t('pages.monitoring.transfers.table.shop', { defaultValue: 'Shop' })}
                value={transfer.shop ?? 'None.'}
              />
              <DetailRow
                label={t('pages.monitoring.transfers.table.operation_account_number', {
                  defaultValue: 'Account Number',
                })}
                value={transfer.opsAccountNumber}
              />
              <DetailRow
                label={t('pages.monitoring.transfers.external_reference', {
                  defaultValue: 'External Reference',
                })}
                value={transfer.externalReference}
              />
              <DetailRow
                label={t('pages.monitoring.transfers.table.amount', { defaultValue: 'Amount' })}
                value={`${transfer.amount} XAF`}
              />
            </List>

            {transfer.status === 0 && hasAny(['edit:cancelTransfer']) && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => void handleCancelClick()}
                    disabled={checkMutation.isPending}
                  >
                    {t('pages.monitoring.transfers.cancel_transfer', {
                      defaultValue: 'Cancel Transfer',
                    })}
                  </Button>
                </Box>
              </>
            )}

            {FAILED_CODES.includes(transfer.status) && hasAny(['edit:correctFailedTransfer']) && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => void handleCorrectClick()}
                    disabled={checkMutation.isPending}
                  >
                    {t('pages.monitoring.transfers.correct_failed_transfer', {
                      defaultValue: 'Correct Failed Transfer',
                    })}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}

        {view === 'cancel' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('pages.monitoring.transfers.ticket_id', { defaultValue: 'Ticket ID' })}
              value={ticketId}
              onChange={(e) => setTicketId(e.target.value)}
              fullWidth
              size="small"
            />
            <Typography variant="body2" color="warning.main">
              {t('pages.monitoring.transfers.cancel_transfer_confirmation', {
                defaultValue: 'Are you sure you want to cancel this transfer?',
              })}
            </Typography>
            <Divider />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="warning"
                onClick={() => setView('details')}
              >
                {t('pages.monitoring.transfers.cancel', { defaultValue: 'Back' })}
              </Button>
              <Button
                variant="contained"
                onClick={() => void handleCancelSend()}
                disabled={cancelMutation.isPending || !ticketId}
              >
                {t('pages.monitoring.transfers.send', { defaultValue: 'Send' })}
              </Button>
            </Stack>
          </Stack>
        )}

        {view === 'correct' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('pages.monitoring.transfers.ticket_id', { defaultValue: 'Ticket ID' })}
              value={ticketId}
              onChange={(e) => setTicketId(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label={t('pages.monitoring.transfers.external_reference', {
                defaultValue: 'External Reference',
              })}
              value={externalReference}
              onChange={(e) => setExternalReference(e.target.value)}
              fullWidth
              size="small"
            />
            <Typography variant="body2" color="warning.main">
              {t('pages.monitoring.transfers.correct_transfer_confirmation', {
                defaultValue: 'Are you sure you want to correct this transfer?',
              })}
            </Typography>
            <Divider />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="warning"
                onClick={() => setView('details')}
              >
                {t('pages.monitoring.transfers.cancel', { defaultValue: 'Back' })}
              </Button>
              <Button
                variant="contained"
                onClick={() => void handleCorrectSend()}
                disabled={correctMutation.isPending || !ticketId}
              >
                {t('pages.monitoring.transfers.send', { defaultValue: 'Send' })}
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
